import { useState, useEffect, useCallback, useRef } from "react";
import { Redirect } from "react-router-dom";
import { Main } from "components";
import useGet from "hooks/useGet";

import { ReactComponent as SettingsIcon } from "icons/settings.svg";
import StatusesList from "./components/StatusesList";
// import ProductsList from "./components/ProductsList";
import GroupedProductsList from "./components/GroupedProductsList";
import { Button } from "expano-components";
import MassEventsTrigger from "./components/MassEventsTrigger";
import { compacted, getData } from "./components/GroupedProductsList/helpers";

const Workflows = ({
  location,
  history,
  match: {
    params: { progress_status },
  },
}) => {
  const load_more_ref = useRef(null);
  const [is_mass_event_enabled, setMassEventEnabled] = useState(false);
  const [mass_events, setMassEvents] = useState([]);
  const [query_filters, setQueryFilters] = useState({});
  const [is_loading, setLoading] = useState(false);
  const [is_refetching, setRefetching] = useState(false);
  const [is_fetching_next_page, setFetchingNextPage] = useState(false);

  const [data, setData] = useState([]);
  const [meta, setMeta] = useState({
    has_next_page: false,
    past_product_group_ids: [],
    after_channel_product_id: null,
  });
  const {
    data: statuses = {},
    is_fetching,
    refetch: refetchStatuses,
  } = useGet({
    query: ["product_workflow_statuses"],
    path: "/channel_product_workflows/statuses",
    error_message_enabled: false,
    onError: () => history.push("/product_groups"),
  });

  useEffect(() => {
    if (!is_mass_event_enabled) {
      setMassEvents([]);
    }
    // eslint-disable-next-line
  }, [is_mass_event_enabled]);

  const getInitialData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getData({ progress_status, query_filters });

      setData(res.data);
      setMeta({
        ...res.meta,
        has_next_page: res.meta.after_channel_product_id > 0,
      });
    } finally {
      setLoading(false);
    }
  }, [progress_status, query_filters]);

  const fetchNextPage = useCallback(async () => {
    try {
      setFetchingNextPage(true);
      const res = await getData({ progress_status, ...meta, query_filters });
      setData([...data, ...res.data]);
      setMeta({
        ...res.meta,
        has_next_page: res.meta.after_channel_product_id > 0,
      });
    } finally {
      setFetchingNextPage(false);
    }
  }, [progress_status, data, meta, query_filters]);

  const refetch = useCallback(
    async (product_group_id) => {
      try {
        setRefetching(true);
        const res = await getData({
          progress_status,
          in_product_group_id: product_group_id,
          query_filters,
        });
        const copy_data = [...data];

        const product_group = res.data?.[0];
        if (product_group) {
          const index = copy_data.findIndex(
            ({ id }) => id === product_group.id,
          );
          if (index > -1) {
            copy_data[index] = product_group;
            setData(copy_data);
          }
        } else {
          const index = copy_data.findIndex(
            ({ id }) => id === Number(product_group_id),
          );
          if (index > -1) {
            copy_data.splice(index, 1);
            setData(copy_data);
          }
        }
      } catch (error) {
        throw error;
      } finally {
        setRefetching(false);
      }
    },
    [progress_status, query_filters, data],
  );

  const handleSetQueryFilters = ({ name, values }) => {
    const query = { ...query_filters };
    query[name] = values;
    setQueryFilters(compacted(query));
  };

  useEffect(() => {
    getInitialData();
    // eslint-disable-next-line
  }, [progress_status, query_filters]);

  useEffect(() => {
    if (!meta.has_next_page || is_fetching_next_page || is_refetching) return;
    if (!load_more_ref.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting) {
          fetchNextPage();
        }
      },
      { threshold: 1.0 }, // widoczny w 100%
    );

    observer.observe(load_more_ref.current);

    return () => {
      if (load_more_ref.current) {
        // eslint-disable-next-line
        observer.unobserve(load_more_ref.current);
      }
    };
  }, [
    load_more_ref,
    meta.has_next_page,
    is_fetching_next_page,
    is_refetching,
    is_loading,
    fetchNextPage,
  ]);

  if (!progress_status) {
    return <Redirect to="/workflows/pending" />;
  }

  return (
    <Main
      page={{
        name: "Statusy produktów",
        icon: <SettingsIcon />,
        breadcrumbs: [{ name: "Statusy produktów" }],
        buttons: (
          <>
            <Button
              text={`${!is_mass_event_enabled ? "Włącz" : "Wyłącz"} tryb aktualizacji masowej`}
              onClick={() => setMassEventEnabled(!is_mass_event_enabled)}
            />
            {is_mass_event_enabled && (
              <MassEventsTrigger
                mass_events={mass_events}
                setMassEvents={setMassEvents}
                refetch={getInitialData}
                refetchStatuses={refetchStatuses}
              />
            )}
          </>
        ),
      }}
    >
      <div className="product-workflow-layout">
        {!is_mass_event_enabled && (
          <StatusesList
            data={statuses}
            refetch={refetchStatuses}
            is_refetching={is_fetching}
            progress_status={progress_status}
          />
        )}
        <div className="products-list">
          <GroupedProductsList
            {...{
              progress_status,
              location,
              history,
              refetchStatuses,
              is_mass_event_enabled,
              setMassEventEnabled,
              mass_events,
              setMassEvents,
              query_filters,
              setQueryFilters,
              refetch,
              load_more_ref,
              handleSetQueryFilters,
              data,
              meta,
              fetchNextPage,
              is_fetching_next_page,
              is_refetching,
            }}
          />
        </div>
      </div>
    </Main>
  );
};

export default Workflows;
